import { useEffect, useState } from 'react';
import { Alert, Checkbox, ControlLabel, FormControl, FormGroup, HelpBlock, Modal } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { UserGroup } from '@nkr/enums/user-group';
import { IOpoSaveResult } from '@nkr/models/opo-document';
import { IOpoContactPosition } from '@nkr/models/opo-contact';
import { IOpoPortalUser, IOpoUserAddRequest, IOpoUserUpdateRequest } from '@nkr/models/opo-portal-users';
import { OpoModalButtons, OpoModalForm } from '../../opo-styles';
import ContactRoleCheckboxes from '../contacts/contact-role-checkboxes';

export const roleOptions = [
    { value: UserGroup.Opo, label: 'OPO User' },
    { value: UserGroup.OpoAdmin, label: 'OPO Admin' }
];

const nameField = (label: string) => z.string().trim().min(1, `${label} is required`).max(64, `${label} cannot be longer than 64 characters`);

const usernameField = z.string().trim().min(1, 'Username is required').max(32, 'Username cannot be longer than 32 characters').regex(/^\S+$/, 'Username cannot contain spaces');

const contactPositionsField = z.array(z.number()).min(1, 'Select at least one contact role');

const addSchema = z.object({
    email: z.string().trim().min(1, 'Email is required').max(64, 'Email cannot be longer than 64 characters').email('Please enter a valid email address'),
    username: usernameField,
    firstName: nameField('First name'),
    lastName: nameField('Last name'),
    groupId: z.string(),
    contactPositions: contactPositionsField
});

const passwordPattern = /(?!^[0-9]*$)(?!^[a-z]*$)(?!^[A-Z]*$)^(.{8,})$/;

const editSchema = z.object({
    firstName: nameField('First name'),
    lastName: nameField('Last name'),
    username: usernameField,
    groupId: z.string(),
    contactPositions: contactPositionsField,
    password: z.string().refine(value => value == '' || passwordPattern.test(value), 'Password must be at least 8 characters and include a mix of numbers, lowercase and uppercase letters'),
    confirmPassword: z.string(),
    requirePasswordChange: z.boolean()
}).refine(x => x.password == '' || x.password == x.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword']
});

type AddFormValues = z.infer<typeof addSchema>;
type EditFormValues = z.infer<typeof editSchema>;

const emptyAdd: AddFormValues = { email: '', username: '', firstName: '', lastName: '', groupId: String(UserGroup.Opo), contactPositions: [] };

const renderGroup = (label: string, error: string | undefined, input: JSX.Element, className?: string, required = false) => (
    <FormGroup className={[className, error ? 'has-error' : null].filter(x => x).join(' ')}>
        <ControlLabel>{label}{required && <span className='required' aria-hidden='true'> *</span>}</ControlLabel>
        {input}
        {error && (
            <HelpBlock>{error}</HelpBlock>
        )}
    </FormGroup>
);

const renderRoleSelect = (value: string, onChange: (e: any) => void, disabled = false) => (
    <FormControl componentClass='select' value={value} onChange={onChange} disabled={disabled}>
        {roleOptions.map(role =>
            <option key={role.value} value={role.value}>{role.label}</option>
        )}
    </FormControl>
);

interface IAddProps {
    show: boolean,
    positions: IOpoContactPosition[],
    isEmailAvailable: (email: string) => Promise<boolean>,
    onAdd: (request: IOpoUserAddRequest) => Promise<IOpoSaveResult>,
    onCancel: () => void
}

export const AddUserModal = ({ show, positions, isEmailAvailable, onAdd, onCancel }: IAddProps) => {
    const [serverError, setServerError] = useState('');
    const { control, handleSubmit, reset, setError, formState: { errors, isSubmitting } } = useForm<AddFormValues>({
        resolver: zodResolver(addSchema),
        defaultValues: emptyAdd
    });

    useEffect(() => {
        if (show) {
            reset(emptyAdd);
            setServerError('');
        }
    }, [show]);

    const onSubmit = async (values: AddFormValues) => {
        setServerError('');

        if (!await isEmailAvailable(values.email)) {
            setError('email', { message: 'That email address is already used by another user or invitation' });
            return;
        }

        const result = await onAdd({
            email: values.email,
            username: values.username,
            firstName: values.firstName,
            lastName: values.lastName,
            groupId: Number(values.groupId),
            contactPositions: values.contactPositions
        });

        if (!result.success) {
            setServerError(result.message);
        }
    };

    return (
        <Modal show={show} onHide={onCancel} backdrop='static'>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Modal.Header closeButton>
                    <Modal.Title>Add User</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <OpoModalForm>
                        {serverError && (
                            <Alert bsStyle='danger'>{serverError}</Alert>
                        )}

                        <div className='form-grid'>
                            <Controller control={control} name='firstName' render={({ field }) =>
                                renderGroup('First Name', errors.firstName?.message, <FormControl type='text' maxLength={64} required aria-required='true' value={field.value} onChange={field.onChange} onBlur={field.onBlur} />, undefined, true)
                            } />
                            <Controller control={control} name='lastName' render={({ field }) =>
                                renderGroup('Last Name', errors.lastName?.message, <FormControl type='text' maxLength={64} required aria-required='true' value={field.value} onChange={field.onChange} onBlur={field.onBlur} />, undefined, true)
                            } />
                            <Controller control={control} name='email' render={({ field }) =>
                                renderGroup('Email', errors.email?.message, <FormControl type='email' maxLength={64} required aria-required='true' value={field.value} onChange={field.onChange} onBlur={field.onBlur} />, 'full-width', true)
                            } />
                            <Controller control={control} name='username' render={({ field }) =>
                                renderGroup('User Name', errors.username?.message, <FormControl type='text' maxLength={32} autoComplete='off' required aria-required='true' value={field.value} onChange={field.onChange} onBlur={field.onBlur} />, 'full-width', true)
                            } />
                            <Controller control={control} name='groupId' render={({ field: { value, onChange } }) =>
                                renderGroup('User Role', errors.groupId?.message, renderRoleSelect(value, onChange), undefined, true)
                            } />
                            <Controller control={control} name='contactPositions' render={({ field: { value, onChange } }) =>
                                renderGroup('Contact Roles', errors.contactPositions?.message, <ContactRoleCheckboxes positions={positions} value={value} onChange={onChange} />, 'full-width', true)
                            } />
                        </div>

                        <p style={{ margin: '14px 0 14px', fontSize: 13, color: '#6B7A8F' }}>
                            <span className='required'>*</span> Required
                        </p>

                        <p className='notice'>
                            We will send an invitation email with a link so that the new user can verify their account and set a password. The link in the email will expire in 14 days.
                        </p>
                    </OpoModalForm>
                </Modal.Body>
                <Modal.Footer>
                    <OpoModalButtons>
                        <button type='submit' className='btn btn-primary' disabled={isSubmitting}>Add User</button>
                        <button type='button' className='btn' onClick={onCancel}>Cancel</button>
                    </OpoModalButtons>
                </Modal.Footer>
            </form>
        </Modal>
    );
}

interface IEditProps {
    user: IOpoPortalUser | null,
    positions: IOpoContactPosition[],
    isSelf: boolean,
    onSave: (userId: number, request: IOpoUserUpdateRequest) => Promise<IOpoSaveResult>,
    onCancel: () => void
}

export const EditUserModal = ({ user, positions, isSelf, onSave, onCancel }: IEditProps) => {
    const [serverError, setServerError] = useState('');
    const { control, handleSubmit, reset, watch, formState: { errors, isSubmitting } } = useForm<EditFormValues>({
        resolver: zodResolver(editSchema)
    });

    useEffect(() => {
        if (user) {
            reset({ firstName: user.firstName, lastName: user.lastName, username: user.username, groupId: String(user.groupId), contactPositions: user.contactPositions, password: '', confirmPassword: '', requirePasswordChange: true });
            setServerError('');
        }
    }, [user]);

    const password = watch('password');

    const onSubmit = async (values: EditFormValues) => {
        setServerError('');

        const result = await onSave(user.id, {
            firstName: values.firstName,
            lastName: values.lastName,
            username: values.username,
            groupId: Number(values.groupId),
            contactPositions: values.contactPositions,
            password: values.password,
            requirePasswordChange: values.requirePasswordChange
        });

        if (!result.success) {
            setServerError(result.message);
        }
    };

    return (
        <Modal show={user != null} onHide={onCancel} backdrop='static'>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Modal.Header closeButton>
                    <Modal.Title>Edit User</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <OpoModalForm>
                        {serverError && (
                            <Alert bsStyle='danger'>{serverError}</Alert>
                        )}

                        <div className='form-grid'>
                            <Controller control={control} name='firstName' render={({ field }) =>
                                renderGroup('First Name', errors.firstName?.message, <FormControl type='text' maxLength={64} required aria-required='true' value={field.value} onChange={field.onChange} onBlur={field.onBlur} />, undefined, true)
                            } />
                            <Controller control={control} name='lastName' render={({ field }) =>
                                renderGroup('Last Name', errors.lastName?.message, <FormControl type='text' maxLength={64} required aria-required='true' value={field.value} onChange={field.onChange} onBlur={field.onBlur} />, undefined, true)
                            } />
                            {renderGroup('Email', undefined, <FormControl type='text' readOnly value={user?.email || ''} />, 'full-width')}
                            <Controller control={control} name='username' render={({ field }) =>
                                renderGroup('User Name', errors.username?.message, <FormControl type='text' maxLength={32} autoComplete='off' required aria-required='true' value={field.value} onChange={field.onChange} onBlur={field.onBlur} />, 'full-width', true)
                            } />
                            <Controller control={control} name='groupId' render={({ field: { value, onChange } }) =>
                                renderGroup('User Role', errors.groupId?.message, renderRoleSelect(value, onChange, isSelf), undefined, true)
                            } />
                            <Controller control={control} name='contactPositions' render={({ field: { value, onChange } }) =>
                                renderGroup('Contact Roles', errors.contactPositions?.message, <ContactRoleCheckboxes positions={positions} value={value} onChange={onChange} />, 'full-width', true)
                            } />
                            <Controller control={control} name='password' render={({ field }) =>
                                renderGroup('New Password', errors.password?.message, <FormControl type='password' autoComplete='new-password' placeholder='Leave blank to keep current' value={field.value} onChange={field.onChange} onBlur={field.onBlur} />)
                            } />
                            <Controller control={control} name='confirmPassword' render={({ field }) =>
                                renderGroup('Confirm Password', errors.confirmPassword?.message, <FormControl type='password' autoComplete='new-password' disabled={!password} required={!!password} aria-required={!!password} value={field.value} onChange={field.onChange} onBlur={field.onBlur} />, undefined, !!password)
                            } />
                            {password && (
                                <Controller control={control} name='requirePasswordChange' render={({ field: { value, onChange } }) =>
                                    <Checkbox className='full-width' checked={value} onChange={(e: any) => onChange(e.target.checked)} style={{ margin: 0 }}>
                                        Require a new password at next sign-in
                                    </Checkbox>
                                } />
                            )}
                        </div>

                        <p style={{ margin: '14px 0 0', fontSize: 13, color: '#6B7A8F' }}>
                            <span className='required'>*</span> Required
                        </p>

                        {isSelf && (
                            <p style={{ margin: '14px 0 0', fontSize: 13, color: '#6B7A8F' }}>You can't change your own role.</p>
                        )}
                    </OpoModalForm>
                </Modal.Body>
                <Modal.Footer>
                    <OpoModalButtons>
                        <button type='submit' className='btn btn-primary' disabled={isSubmitting}>Save Changes</button>
                        <button type='button' className='btn' onClick={onCancel}>Cancel</button>
                    </OpoModalButtons>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
